import { createAsyncThunk } from '@reduxjs/toolkit';
import { PayoutService } from '../services/payout.service';
import { TaxProfile } from '../types/payout.types';
import { showCriticalAlert } from './common.actions';
import i18n from '../i18n';

export const fetchConsentUrl = createAsyncThunk(
    'prize/fetchConsentUrl',
    async (
        preload: { locale: string; prizeId: string; dob?: string },
        { rejectWithValue, dispatch }
    ) => {
        try {
            if (!preload?.dob) {
                // remove dob when passed null
                delete (preload as any).dob;
            }
            const taxInterviewData = await PayoutService.fetchConsentUrl(preload);
            dispatch(traceTakeInterview() as any);
            return taxInterviewData;
        } catch (error) {
            dispatch(showCriticalAlert(i18n.t('epic.common.network.error')));
            return rejectWithValue(
                error instanceof Error ? error.message : 'Unknown error occurred'
            );
        }
    }
);

export const fetchTaxProfile = createAsyncThunk(
    'prize/fetchTaxProfile',
    async (_, { rejectWithValue, dispatch }) => {
        try {
            const taxProfileData = await PayoutService.fetchTaxProfile();
            return taxProfileData;
        } catch (error) {
            dispatch(showCriticalAlert(i18n.t('epic.common.network.error')));
            return rejectWithValue(
                error instanceof Error ? error.message : 'Unknown error occurred'
            );
        }
    }
);

export const fetchInterview = createAsyncThunk(
    'prize/fetchInterview',
    async (preload: { locale: string; dob?: string }, { rejectWithValue, dispatch }) => {
        try {
            if (!preload?.dob) {
                // remove dob when passed null
                delete (preload as any).dob;
            }
            const taxInterviewData = await PayoutService.fetchInterview(preload);
            dispatch(traceTakeInterview() as any);
            return taxInterviewData;
        } catch (error) {
            dispatch(showCriticalAlert(i18n.t('epic.common.network.error')));
            return rejectWithValue(
                error instanceof Error ? error.message : 'Unknown error occurred'
            );
        }
    }
);

export const updateTaxProfile = createAsyncThunk(
    'prize/updateTaxProfile',
    async (profileData: Partial<TaxProfile>, { rejectWithValue, dispatch }) => {
        try {
            const updatedProfile = await PayoutService.updateTaxProfile(profileData);
            return updatedProfile;
        } catch (error) {
            dispatch(showCriticalAlert(i18n.t('epic.common.network.error')));
            return rejectWithValue(
                error instanceof Error ? error.message : 'Unknown error occurred'
            );
        }
    }
);

export const fetchAccountStatus = createAsyncThunk(
    'prize/fetchAccountStatus',
    async (_, { rejectWithValue, dispatch }) => {
        try {
            const accountStatusData = await PayoutService.fetchAccountStatus();
            return accountStatusData;
        } catch (error) {
            dispatch(showCriticalAlert(i18n.t('epic.common.network.error')));
            return rejectWithValue(
                error instanceof Error ? error.message : 'Unknown error occurred'
            );
        }
    }
);

export const fetchRetakeAvailable = createAsyncThunk(
    'prize/fetchRetakeAvailable',
    async (_, { rejectWithValue, dispatch }) => {
        try {
            const retakeAvailable = await PayoutService.fetchRetakeAvailable();
            return { retakeAvailable };
        } catch (error) {
            dispatch(showCriticalAlert(i18n.t('epic.common.network.error')));
            return rejectWithValue(
                error instanceof Error ? error.message : 'Unknown error occurred'
            );
        }
    }
);

export const fetchTaxDocuments = createAsyncThunk(
    'prize/fetchTaxDocuments',
    async (_, { rejectWithValue }) => {
        try {
            const taxDocumentsData = await PayoutService.fetchTaxDocuments();
            return taxDocumentsData;
        } catch (error) {
            return rejectWithValue(
                error instanceof Error ? error.message : 'Unknown error occurred'
            );
        }
    }
);

export const traceTakeInterview = createAsyncThunk('prize/traceTakeInterview', () => {
    PayoutService.traceTakeInterview();
    return { success: true };
});

export const checkEmailExisted = createAsyncThunk(
    'prize/checkEmailExisted',
    async (email: string, { rejectWithValue, dispatch }) => {
        try {
            const response = await PayoutService.checkEmailExisted(email);
            return { emailExisted: response.data?.emailExisted };
        } catch (error) {
            dispatch(showCriticalAlert(i18n.t('epic.common.network.error')));
            return rejectWithValue(
                error instanceof Error ? error.message : 'Unknown error occurred'
            );
        }
    }
);

export { setSelectedTaxYear } from '../reducer/tax.reducer';
